// The chat chart window's charts on TradingView Lightweight Charts (v5; compared
// with Plotly & co in rflx-backend/experiments/next_chart). A new or changed
// live bar goes through series.update(), so the user's zoom / scroll stays and
// nothing is redrawn from scratch; setData only runs on the first data and
// when the backend resubscribed the chart (new resetKey = subId).
// The time scale is index based: nights, weekends and holidays leave no gaps.

import {
  CandlestickData,
  CandlestickSeries,
  ColorType,
  createChart,
  CrosshairMode,
  HistogramSeries,
  IChartApi,
  IPriceLine,
  ISeriesApi,
  LineSeries,
  LineStyle,
  PriceScaleMode,
  Time,
  UTCTimestamp,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { Bar, IvPoint } from "./types";
import { C, fmt } from "./ui";

const UP = "#17BECF";
const DOWN = "#7F7F7F";
const PRICE_LINE = "#7F7F7F";
const VOLUME = "rgba(31, 119, 180, 0.45)";
const IV_LINE = "#1f77b4";
const GRID = "#EEEEEE";
const AXIS = "#CCCCCC";
// rising candles outside regular trading hours (pre-market / after-hours): the
// Real-time chart's green; falling ones stay DOWN
const EXT_UP = "#2ECC71";

// ---- time ------------------------------------------------------------------------

const NY_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
});
const nyCache = new Map<number, string>();
// "2026-10-29 09:30:00", New York wall time of a unix time
export function nyTime(t: number) {
  let s = nyCache.get(t);
  if (s === undefined) {
    const p = Object.fromEntries(NY_PARTS.formatToParts(new Date(t * 1000)).map((x) => [x.type, x.value]));
    s = `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
    nyCache.set(t, s);
  }
  return s;
}

// Lightweight Charts shows times as UTC: New York wall time passed as if it were UTC
function wall(t: number) {
  const s = nyTime(t);
  return (Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10), +s.slice(11, 13), +s.slice(14, 16), +s.slice(17, 19)) / 1000) as UTCTimestamp;
}

// a bar starting before 9:30 or from 16:00 New York
function outsideRth(t: number) {
  const time = nyTime(t).slice(11, 16);
  return time < "09:30" || time >= "16:00";
}

// ---- shared ----------------------------------------------------------------------

// Lightweight Charts lays out its panes and axes in a <table>: the app's global
// table styles (top margin, cell borders and padding) would push the time axis
// out of view, so they are reset inside the chart containers
export const CONTAINER = "ibkr-lw-chart";
const RESET_CSS = `.${CONTAINER} table{margin:0!important;border-spacing:0!important}.${CONTAINER} td,.${CONTAINER} th{border:0!important;padding:0!important}`;
export function useResetCss() {
  useEffect(() => {
    if (document.getElementById(CONTAINER)) return;
    const style = document.createElement("style");
    style.id = CONTAINER;
    style.textContent = RESET_CSS;
    document.head.appendChild(style);
  }, []);
}

function newChart(el: HTMLElement, barSize: string) {
  const intraday = !barSize.includes("day") && !barSize.includes("week");
  return createChart(el, {
    autoSize: true,
    layout: {
      background: { type: ColorType.Solid, color: "#FFFFFF" },
      textColor: "#333333",
      fontSize: 11,
      panes: { separatorColor: GRID, separatorHoverColor: GRID },
    },
    grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: { color: "#999999", width: 1, style: LineStyle.Solid, labelBackgroundColor: "#999999" },
      horzLine: { color: "#999999", width: 1, style: LineStyle.Solid, labelBackgroundColor: "#999999" },
    },
    rightPriceScale: { borderColor: AXIS },
    // minBarSpacing: the whole history fits when zoomed out
    timeScale: { borderColor: AXIS, timeVisible: intraday, secondsVisible: barSize.includes("secs"), rightOffset: 3, minBarSpacing: 0.01 },
  });
}

// Applies rows to the series: the first time (or after a reset) all of them,
// afterwards only the rows from the last one shown on (series.update keeps the view)
function useRows<T extends { t: number }>(rows: T[], resetKey: number, apply: (rows: T[], all: boolean) => void, chart: IChartApi | null) {
  const shown = useRef<{ key: number; last: number | null } | null>(null);
  useEffect(() => {
    if (!chart) return;
    const prev = shown.current;
    const last = rows.length ? rows[rows.length - 1].t : null;
    let from = -1;
    if (prev && prev.key === resetKey && prev.last != null) {
      from = rows.length;
      while (from > 0 && rows[from - 1].t >= prev.last) from--;
      if (from === rows.length) from = -1;
    }
    if (from >= 0) apply(rows.slice(from), false);
    else {
      apply(rows, true);
      chart.timeScale().fitContent();
    }
    shown.current = { key: resetKey, last };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, resetKey, chart]);
}

// the dashed latest-price line with its axis tag, like the Real-time chart's
export function usePriceLine(series: ISeriesApi<any> | null, price: number | null | undefined, color = PRICE_LINE) {
  const line = useRef<IPriceLine | null>(null);
  const owner = useRef<ISeriesApi<any> | null>(null);
  useEffect(() => {
    // a recreated series has none of the old one's lines
    if (owner.current !== series) line.current = null;
    owner.current = series;
    if (!series) return;
    if (price == null) {
      if (line.current) series.removePriceLine(line.current);
      line.current = null;
    } else if (line.current) {
      line.current.applyOptions({ price });
    } else {
      line.current = series.createPriceLine({
        price, color, lineWidth: 1, lineStyle: LineStyle.Dashed,
        axisLabelVisible: true, axisLabelColor: color, axisLabelTextColor: "#FFFFFF", title: "",
      });
    }
  }, [series, price, color]);
}

const legendStyle = { position: "absolute" as const, top: 4, left: 8, zIndex: 3, fontSize: 11, color: C.text, background: "rgba(255,255,255,0.8)", pointerEvents: "none" as const, whiteSpace: "nowrap" as const };

// ---- candles ---------------------------------------------------------------------

export function LwCandles(props: { bars: Bar[]; resetKey: number; barSize: string; height: number; price?: number | null; volume?: boolean; logVolume?: boolean }) {
  const { bars, resetKey, barSize, height, price, volume = true, logVolume = false } = props;
  const el = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState<IChartApi | null>(null);
  const series = useRef<{ candles: ISeriesApi<"Candlestick">; volume: ISeriesApi<"Histogram"> | null } | null>(null);
  const byTime = useRef(new Map<number, Bar>());
  const [legend, setLegend] = useState<Bar | null>(null);
  const intraday = !barSize.includes("day") && !barSize.includes("week");
  useResetCss();

  useEffect(() => {
    const c = newChart(el.current!, barSize);
    const candles = c.addSeries(CandlestickSeries, {
      upColor: UP, downColor: DOWN, borderUpColor: UP, borderDownColor: DOWN, wickUpColor: UP, wickDownColor: DOWN,
      priceLineVisible: false, lastValueVisible: false,
    }, 0);
    const vol = volume ? c.addSeries(HistogramSeries, { color: VOLUME, priceFormat: { type: "volume" }, priceLineVisible: false, lastValueVisible: false }, 1) : null;
    if (vol) {
      c.panes()[0]?.setStretchFactor(0.78);
      c.panes()[1]?.setStretchFactor(0.22);
    }
    c.subscribeCrosshairMove((p) => setLegend(p.time !== undefined ? byTime.current.get(p.time as number) ?? null : null));
    series.current = { candles, volume: vol };
    setChart(c);
    return () => {
      series.current = null;
      c.remove();
    };
  }, [barSize, volume]);

  useRows(bars, resetKey, (rows, all) => {
    const s = series.current;
    if (!s) return;
    const point = (b: Bar): CandlestickData<Time> => {
      const p: CandlestickData<Time> = { time: wall(b.t), open: b.o, high: b.h, low: b.l, close: b.c };
      return intraday && b.c >= b.o && outsideRth(b.t) ? { ...p, color: EXT_UP, borderColor: EXT_UP, wickColor: EXT_UP } : p;
    };
    if (all) byTime.current = new Map();
    for (const b of rows) byTime.current.set(wall(b.t), b);
    if (all) {
      s.candles.setData(rows.map(point));
      s.volume?.setData(rows.map((b) => ({ time: wall(b.t), value: b.v ?? 0 })));
    } else {
      for (const b of rows) {
        s.candles.update(point(b));
        s.volume?.update({ time: wall(b.t), value: b.v ?? 0 });
      }
    }
  }, chart);

  usePriceLine(chart ? series.current?.candles ?? null : null, price);

  useEffect(() => {
    series.current?.volume?.priceScale().applyOptions({ mode: logVolume ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal });
  }, [chart, logVolume]);

  const shown = legend ?? bars[bars.length - 1];
  return (
    <div style={{ position: "relative", height }}>
      {shown && (
        <div style={legendStyle}>
          {nyTime(shown.t).slice(0, intraday ? 16 : 10)} · O {fmt(shown.o)} H {fmt(shown.h)} L {fmt(shown.l)} C {fmt(shown.c)} · avg {fmt(shown.avg)} · vol {shown.v ?? "—"}
        </div>
      )}
      <div ref={el} className={CONTAINER} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}

// ---- IV line ---------------------------------------------------------------------

export function LwIv({ points, resetKey, barSize, height }: { points: IvPoint[]; resetKey: number; barSize: string; height: number }) {
  const el = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState<IChartApi | null>(null);
  const series = useRef<ISeriesApi<"Line"> | null>(null);
  const byTime = useRef(new Map<number, IvPoint>());
  const [legend, setLegend] = useState<IvPoint | null>(null);
  useResetCss();

  useEffect(() => {
    const c = newChart(el.current!, barSize);
    series.current = c.addSeries(LineSeries, {
      color: IV_LINE, lineWidth: 1, pointMarkersVisible: true, pointMarkersRadius: 2,
      priceLineVisible: false, lastValueVisible: true,
      priceFormat: { type: "custom", formatter: (v: number) => `${v.toFixed(1)}%`, minMove: 0.1 },
    });
    c.subscribeCrosshairMove((p) => setLegend(p.time !== undefined ? byTime.current.get(p.time as number) ?? null : null));
    setChart(c);
    return () => {
      series.current = null;
      c.remove();
    };
  }, [barSize]);

  useRows(points, resetKey, (rows, all) => {
    const s = series.current;
    if (!s) return;
    if (all) byTime.current = new Map();
    for (const p of rows) byTime.current.set(wall(p.t), p);
    const point = (p: IvPoint) => ({ time: wall(p.t), value: p.iv * 100 });
    if (all) s.setData(rows.map(point));
    else rows.forEach((p) => s.update(point(p)));
  }, chart);

  const shown = legend ?? points[points.length - 1];
  return (
    <div style={{ position: "relative", height }}>
      {shown && (
        <div style={legendStyle}>
          {nyTime(shown.t).slice(0, 16)} · IV {(shown.iv * 100).toFixed(1)}% · option avg {fmt(shown.avg, 3)} · underlying avg {fmt(shown.underlyingAvg, 3)}
        </div>
      )}
      <div ref={el} className={CONTAINER} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}
